import React, { useEffect, useState } from 'react';
import { useData } from '../../hooks/useData';
import { useToast } from '../../hooks/useToast';
import { calculateRisk } from '../../utils/helpers';
import { riskAnalyticsServices } from '../../APIServices/riskAnalyticsServices';
import { usersManagementServices } from '../../APIServices/usersManagementServices';

function RiskCreateModal({ open, onClose, onCreated }) {
  const { state, currentUser, loadStudyList } = useData();
  const toast = useToast();
  const [buttonLoader, setButtonLoader] = useState(false);
  const [ownerList, setOwnerList] = useState([]);
  const [form, setForm] = useState({
    studyId: '',
    title: '',
    p: 3,
    i: 3,
    c: 3,
    owner: currentUser?.id || '',
    dueAt: '',
    status: 'open',
    description: '',
    mitigation: ''
  });

  const calc = calculateRisk(form.p, form.i, form.c);

  const getOwnerValue = (user) => user?.user || user?.userId || user?.id || '';
  const getOwnerLabel = (user) => {
    const fullName = [user?.firstName, user?.middleName, user?.lastName].filter(Boolean).join(' ').trim();
    return fullName || user?.name || user?.email || 'Unknown User';
  };

  const loadOwners = async () => {
    try {
      let data = await usersManagementServices.getUserData('all', 'user', '', 1, 1000);
      let users = data?.data || [];
      setOwnerList(users);
    } catch (error) {
      setOwnerList([]);
      toast('Unable to load owner list.', 'warn');
    }
  };

  useEffect(() => {
    if (open) {
      loadOwners();
      if (state.studies.length === 0) {
        loadStudyList?.();
      }
    }
  }, [open, state.studies.length, loadStudyList]);

  const resetForm = () => {
    setForm({
      studyId: '',
      title: '',
      p: 3,
      i: 3,
      c: 3,
      owner: currentUser?.id || '',
      dueAt: '',
      status: 'open',
      description: '',
      mitigation: ''
    });
  };

  useEffect(() => {
    if (open && !form.owner && currentUser?.id) {
      setForm((prev) => ({ ...prev, owner: currentUser.id }));
    }
  }, [open, currentUser?.id]);

  const submit = () => {
    if (!form.title || !form.studyId) {
      toast('Title and study required', 'warn');
      return;
    }

    createRisk();
  };

  const createRisk = async () => {
    setButtonLoader(true);
    try {
      const payload = {
        studyId: form.studyId,
        title: form.title,
        p: form.p,
        i: form.i,
        c: form.c,
        owner: form.owner,
        status: form.status,
        dueAt: form.dueAt ? new Date(form.dueAt).toISOString() : null,
        description: form.description,
        mitigation: form.mitigation,
      };

      const data = await riskAnalyticsServices.addNewRiskAnalytics(payload);
      toast(`Risk ${data?.data?.code || ''} created`, 'ok');
      resetForm();
      onClose();
      onCreated?.();
    } catch (error) {
      toast('Unable to create risk.', 'warn');
    } finally {
      setButtonLoader(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="modal modal-wide">
        <div className="modal-head">
          <div>
            <div className="modal-title">Create new risk</div>
            <div className="xs muted">Use the P × I × Control model to calculate risk scores automatically.</div>
          </div>
          <button className="icon-btn" onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>

        <div className="modal-body">
          <div className="field">
            <label className="field-label">Risk title <span className="req">*</span></label>
            <input 
              value={form.title} 
              onChange={(e) => setForm({ ...form, title: e.target.value })} 
              placeholder="e.g. EC approval delay, Bioanalytical method not validated"
              autoFocus
            />
          </div>

          <div className="field-row">
            <div className="field">
              <label className="field-label">Study <span className="req">*</span></label>
              <select 
                value={form.studyId} 
                onChange={(e) => setForm({ ...form, studyId: e.target.value })}
              >
                <option value="">Select study...</option>
                {state.studies.map((s) => (
                  <option key={s.id || s.studyId} value={s.id || s.studyId}>
                    {s.code || s.studyCode} — {s.name || s.title || s.code || s.studyCode}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label className="field-label">Owner</label>
              <select 
                value={form.owner} 
                onChange={(e) => setForm({ ...form, owner: e.target.value })}
              >
                {ownerList.map((u) => (
                  <option key={getOwnerValue(u)} value={getOwnerValue(u)}>{getOwnerLabel(u)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="field-row-3">
            <div className="field">
              <label className="field-label">Probability (1–5)</label>
              <input
                type="range"
                min={1}
                max={5}
                step={1}
                value={form.p}
                onChange={(e) => setForm({ ...form, p: +e.target.value })}
                style={{ width: '100%' }}
              />
              <div className="tiny muted" style={{ textAlign: 'center' }}>
                P = {form.p} — {['Very Low', 'Low', 'Medium', 'High', 'Very High'][form.p - 1]}
              </div>
            </div>
            <div className="field">
              <label className="field-label">Impact (1–5)</label>
              <input
                type="range"
                min={1}
                max={5}
                step={1}
                value={form.i}
                onChange={(e) => setForm({ ...form, i: +e.target.value })}
                style={{ width: '100%' }}
              />
              <div className="tiny muted" style={{ textAlign: 'center' }}>
                I = {form.i} — {['Minimal', 'Minor', 'Moderate', 'Major', 'Critical'][form.i - 1]}
              </div>
            </div>
            <div className="field">
              <label className="field-label">Control gap (1–5)</label>
              <input
                type="range"
                min={1}
                max={5}
                step={1}
                value={form.c}
                onChange={(e) => setForm({ ...form, c: +e.target.value })}
                style={{ width: '100%' }}
              />
              <div className="tiny muted" style={{ textAlign: 'center' }}>
                C = {form.c} — {['Robust', 'Adequate', 'Partial', 'Weak', 'None'][form.c - 1]}
              </div>
            </div>
          </div>

          {/* Live Score Display */}
          <div className="card" style={{ background: 'var(--surface-2)', marginBottom: 14, padding: '14px 18px' }}>
            <div className="flex between center">
              <div className="sm bold">Live computed score</div>
              <span className={`tag tag-${calc.variant === 'danger' ? 'danger' : calc.variant === 'warn' ? 'warn' : calc.variant === 'ok' ? 'ok' : 'info'} tag-dot`}>
                {calc.label}
              </span>
            </div>
            <div 
              className="metric-value" 
              style={{ 
                fontSize: 32,
                color: calc.variant === 'danger' ? 'var(--danger)' : 
                       calc.variant === 'warn' ? 'var(--warn)' : 
                       calc.variant === 'ok' ? 'var(--ok)' : 
                       'var(--ink)'
              }}
            >
              {calc.score}
              <span className="mono xs muted" style={{ fontWeight: 400, marginLeft: 8 }}>
                = P{form.p} × I{form.i} × C{form.c}
              </span>
            </div>
            <div className="tiny muted">Score bands: Low ≤20, Medium ≤50, High ≤80, Critical &gt;80</div>
          </div>

          <div className="field">
            <label className="field-label">Description / context</label>
            <textarea 
              value={form.description} 
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Describe the risk, its context, and potential consequences..."
              rows={2}
            />
          </div>

          <div className="field">
            <label className="field-label">Mitigation plan</label>
            <textarea 
              value={form.mitigation} 
              onChange={(e) => setForm({ ...form, mitigation: e.target.value })}
              placeholder="What controls or actions are planned to mitigate this risk?"
              rows={2}
            />
          </div>

          <div className="field">
            <label className="field-label">Due date for mitigation</label>
            <input 
              type="date" 
              value={form.dueAt} 
              onChange={(e) => setForm({ ...form, dueAt: e.target.value })} 
            />
          </div>
        </div>

        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={() => { resetForm(); onClose(); }}>Cancel</button>
          {buttonLoader ? (
            <button className="btn btn-primary" disabled><div className='buttonLoader' /> Create risk</button>
          ) : (
            <button className="btn btn-primary" onClick={submit}>
              <i className="ti ti-check" /> Create risk
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default RiskCreateModal;